import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ModalElement } from '@shared/dream-components';

import Button from '~/components/Button';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (email: string) => void;
  initialEmail?: string;
}

const EmailConfirmationModal = ({ isOpen, onClose, onConfirm, initialEmail = '' }: Props) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  // Reset state when modal opens or initialEmail changes
  useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail);
      setError('');
      setIsSubmitting(false);
    }
  }, [isOpen, initialEmail]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleConfirm = () => {
    if (!email) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    setIsSubmitting(true);
    onConfirm(email);
  };

  const handleClose = () => {
    setError('');
    setIsSubmitting(false);
    onClose();
  };

  const modal = (
    <ModalElement
      element={{ type: 'modal' }}
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      isLoading={isSubmitting}
      showCloseButton
    >
      <div className="bg-white p-6 md:w-[448px]">
        <h3 className="pb-1 pr-8 text-xl font-bold leading-tight tracking-medium text-gray-900">
          {t('stripe.email_confirmation.title')}
        </h3>
        <p className="mb-4 pb-1 text-sm font-light leading-tight tracking-wider text-gray-500">
          {t('stripe.email_confirmation.message')}
        </p>

        <div className="flex flex-col gap-4">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              placeholder={t('stripe.email_confirmation.placeholder')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={handleClose} variant="secondary" disabled={isSubmitting}>
              {t('shared.cta.cancel')}
            </Button>
            <Button onClick={handleConfirm} variant="primary" disabled={isSubmitting}>
              {isSubmitting ? t('shared.actions.submitting') : t('shared.cta.continue')}
            </Button>
          </div>
        </div>
      </div>
    </ModalElement>
  );

  return mounted ? createPortal(modal, document.body) : null;
};

export default EmailConfirmationModal;
